"use client";
import Image from "@/components/Image/Image.component";
import { checkIfImage } from "@/lib/utils";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { ScrollSmoother } from "gsap/ScrollSmoother";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import BackgroundPlayer from "next-video/background-player";
import { useEffect, useRef } from "react";
import Button from "../Button/Button.component";
import { BUTTON_SUBTYPE_CLASS } from "../Button/Button.model";
import { Typography } from "../Typography/Typography.component";
import {
  WorkShowcaseFields,
  WorkShowcasePropsModel,
} from "./WorkShowcase.model";

gsap.registerPlugin(ScrollTrigger, ScrollSmoother, useGSAP);

const WorkShowcase = (items: WorkShowcasePropsModel) => {
  const workShowcaseVideoRef = useRef<HTMLVideoElement>(null);
  const showcaseItems = items;

  useEffect(() => {
    if (workShowcaseVideoRef?.current) {
      workShowcaseVideoRef.current.setAttribute("playsinline", "");
    }
  }, []);

  useGSAP(() => {
    const panels = gsap.utils.toArray<HTMLElement>(".showcase__item");
    const mm = gsap.matchMedia();
    mm.add(`(max-width: 767px)`, () => {
      ScrollSmoother.create({ smoothTouch: 0.1 });
    });
    panels.forEach((panel, i) => {
      ScrollTrigger.create({
        trigger: panel,
        start: "top top",
        pin: i != panels.length - 1 && ".header",
        pinSpacing: false,
      });
      ScrollTrigger.create({
        trigger: panel,
        start: "top top",
        pin: i != panels.length - 1,
        pinSpacing: false,
      });
      const content = panel.querySelector(".showcase__item__content");
      gsap.fromTo(
        content,
        { y: 100, visibility: "hidden" },
        {
          y: 0,
          duration: 1,
          visibility: "visible",
          ease: "power2.out",
          scrollTrigger: {
            trigger: panel,
            start: "top center",
            scrub: true,
          },
        },
      );
    });
  }, []);

  return (
    <div className="showcase border-core-red relative col-span-4 grid overflow-hidden border-(length:--spacing-xxs) border-solid md:col-span-8 md:border-(length:--spacing-sm)">
      <div className="header px-sm pt-lg md:p-md lg:p-lg absolute top-0 right-0 left-0 z-50 flex w-full justify-between">
        <Typography.H6 className="!text-core-white text-body-regular flex items-center">
          OUR WORK
        </Typography.H6>
        <Button
          primary={true}
          type={BUTTON_SUBTYPE_CLASS.PRIMARY_WHITE_OUTLINE}
          content={{ text: "VIEW ALL", url: "work" }}
        />
      </div>
      {showcaseItems.items.map((item: WorkShowcaseFields, index: number) => {
        return (
          <div
            key={item.name}
            className="showcase__item relative h-screen w-full"
          >
            {checkIfImage(item?.workShowcaseBackground?.contentType) ? (
              <Image
                src={`${item?.workShowcaseBackground?.url}?fm=webp`}
                alt={item.name}
                width={0}
                height={0}
                className="absolute h-full w-full object-cover"
                hasPlaceholder
              />
            ) : (
              <BackgroundPlayer
                ref={workShowcaseVideoRef}
                src={item?.workShowcaseBackground.url}
                controls={false}
                className="absolute [&>.next-video-bg-video]:h-screen [&>.next-video-bg-video]:w-screen"
              />
            )}
            <div className="showcase__item__content px-sm pt-lg md:p-md lg:p-lg lg:pb-xxxxxl absolute z-50 flex h-screen w-full flex-col justify-between pb-[8rem] md:pb-[8rem]">
              <div></div>
              <div className="gap-sm text-core-white gap-y-md grid w-full">
                <div className="py-xxs grid grid-cols-2 gap-[3.5rem] lg:grid-cols-4">
                  <div>
                    <Typography.LLABEL2>{item.name}</Typography.LLABEL2>
                  </div>
                  <div>
                    <Typography.LLABEL2>{item.brand}</Typography.LLABEL2>
                  </div>
                  <div className="col-span-2 flex lg:col-span-2 lg:justify-end">
                    <Button
                      className="min-w-[112px]"
                      key={index}
                      primary
                      type={BUTTON_SUBTYPE_CLASS.PRIMARY_WHITE}
                      content={{ text: "VIEW WORK", url: `work/${item.slug}` }}
                    />
                  </div>
                </div>
              </div>
              <div className="text-core-white grid grid-cols-1 md:grid-cols-3 lg:grid-cols-12">
                <Typography.H3Sentence
                  className={`leading-none !normal-case md:col-span-1 md:!text-[52px] lg:col-span-5 ${showcaseItems.items.length - 1 > index ? "pb-5" : "pb-2"}`}
                >
                  {item?.workShowcaseTitle}
                </Typography.H3Sentence>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
export default WorkShowcase;
